import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  ROLE_LABELS,
  SYSTEM_EMAIL_ELIGIBLE_ROLES,
  systemEmailSelectionWarning,
  type SystemEmailCandidate,
  type SystemEmailSettings,
} from "@xop/dashboard-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { PageLoader } from "@/components/ui/page-loader";
import { toast } from "@/components/ui/use-toast";
import { ApiError } from "../../../lib/api.js";
import { SYSTEM_EMAILS_QUERY_KEY, systemEmailsApi } from "./api.js";

/** What a group of candidates is called on the page — "admins or salespeople". */
const GROUP_LABELS: Record<SystemEmailCandidate["dashboard_role"], string> = {
  super_admin: "Admins",
  sales_manager: "Salespeople",
};

const sameSet = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/**
 * System Admin ▸ System Emails (`/admin/system-emails`).
 *
 * ONE checklist (CEO, 2026-09-17): the people checked receive EVERY system
 * email — licence approval requests, reseller quotes, purchase orders, reseller
 * applications, unsubscribe sync alerts — and the people not checked receive none. NOBODY checked means
 * NO system email is sent (CEO, 2026-09-17: the old "every super admin" fallback
 * mailed five admins from an all-unchecked page), and the page warns in words.
 * Licence approval requests go only to checked super admins, so a list with no
 * super admin gets a warning too — a warning, never a block. There are
 * deliberately no per-email lists.
 *
 * crm-api sends these emails, so it owns the list (`SystemEmails::Recipients`);
 * this page reads and writes it through `/api/admin/system-emails`.
 *
 * No own <PageHeader> — the shared "System Admin" header + tab strip live in
 * AdminLayout.tsx (same contract as the other tabs).
 */
export function SystemEmailsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: SYSTEM_EMAILS_QUERY_KEY,
    queryFn: systemEmailsApi.load,
  });

  const [picked, setPicked] = useState<number[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  // A save (or another admin's change, on refetch) replaces the draft.
  useEffect(() => {
    if (data) setPicked(data.user_ids);
  }, [data]);

  const save = useMutation({
    mutationFn: (userIds: number[]) => systemEmailsApi.save(userIds),
    onSuccess: (settings: SystemEmailSettings) => {
      queryClient.setQueryData(SYSTEM_EMAILS_QUERY_KEY, settings);
      setSaveError(null);
      toast({
        title: "Recipients saved",
        description:
          settings.effective_emails.length === 0
            ? "Nobody receives the system emails."
            : `System emails go to: ${settings.effective_emails.join(", ")}`,
      });
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof ApiError || err instanceof Error ? err.message : "Could not save the recipients.");
    },
  });

  const warning = useMemo(
    () => (data ? systemEmailSelectionWarning(picked, data.candidates) : null),
    [data, picked],
  );

  if (isLoading) return <PageLoader />;

  if (isError || !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {error instanceof Error ? error.message : "Could not load the system email recipients."}
      </Alert>
    );
  }

  const dirty = !sameSet(picked, data.user_ids);
  const saving = save.isPending;
  const toggle = (id: number) =>
    setPicked((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  return (
    <Box sx={{ display: "grid", gap: 2, maxWidth: 1100 }}>
      <Box>
        <Typography variant="h5">System emails</Typography>
        <Typography variant="body2" color="text.secondary">
          Check the people who receive the system emails. Everyone checked receives all of them; people who are not
          checked receive none.
        </Typography>
      </Box>

      <Card data-testid="system-email-recipients">
        <CardContent sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ flex: "1 1 320px" }}>
              <Typography variant="h6">Recipients</Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                These people receive:
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }} data-testid="system-email-kinds">
                  {data.emails.map((kind) => (
                    <li key={kind.key}>
                      <strong>{kind.label}</strong> — {kind.description}
                    </li>
                  ))}
                </Box>
              </Typography>
            </Box>
            <Chip
              size="small"
              color={data.user_ids.length === 0 ? "default" : "primary"}
              label={data.user_ids.length === 0 ? "Nobody checked" : `${data.user_ids.length} checked`}
              data-testid="system-email-status"
            />
          </Box>

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
            {SYSTEM_EMAIL_ELIGIBLE_ROLES.map((role) => {
              const group = data.candidates.filter((c) => c.dashboard_role === role);
              return (
                <Box key={role}>
                  <Typography variant="overline" color="text.secondary">
                    {GROUP_LABELS[role]}
                  </Typography>
                  {group.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No active {ROLE_LABELS[role].toLowerCase()} accounts.
                    </Typography>
                  ) : (
                    <Box sx={{ display: "grid" }}>
                      {group.map((person) => (
                        <FormControlLabel
                          key={person.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={picked.includes(person.id)}
                              onChange={() => toggle(person.id)}
                              disabled={saving}
                            />
                          }
                          label={
                            <Box component="span">
                              {person.name || person.email}{" "}
                              <Typography component="span" variant="body2" color="text.secondary">
                                {person.name ? person.email : ""}
                              </Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {warning ? (
            <Alert severity="warning" data-testid="system-email-warning">
              {warning}
            </Alert>
          ) : null}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: "1 1 320px" }}
              data-testid="system-email-effective"
            >
              {data.effective_emails.length > 0
                ? `The system emails go only to: ${data.effective_emails.join(", ")}`
                : "The system emails go to nobody."}
            </Typography>
            {dirty ? (
              <Button size="small" onClick={() => setPicked(data.user_ids)} disabled={saving}>
                Undo changes
              </Button>
            ) : null}
            <Button
              variant="contained"
              size="small"
              disabled={!dirty || saving}
              onClick={() => save.mutate(picked)}
            >
              Save
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
